import { useCallback, useEffect, useRef, useState } from 'react';
import {
  areStepsFinal,
  fetchPatientPipelineStatus,
  isPatientPipelineApiAvailable,
  type PatientPipelineStatus,
} from '../../api/patientPipelineApi';

interface UsePipelineStatus {
  status: PatientPipelineStatus | null;
  loading: boolean;
  error: string | null;
  ready: boolean;
  reload: () => void;
}

/**
 * Polls the patient pipeline-status endpoint after a SMART launch so the
 * workspace can render live "preparing…" states while the clockwork pipeline
 * (twin + initial heartbeat) runs in the background. Polls quickly until ready,
 * then drops to a light steady-state poll that keeps risk/alert counts fresh.
 */
export function usePatientPipelineStatus(
  patientId: string | undefined,
  opts: {
    activeIntervalMs?: number;
    idleIntervalMs?: number;
    /**
     * Ticket #38 recurrence: `ready` reflects Digital-Twin readiness only,
     * not the step journal -- on a fresh SMART launch it can flip `true` on
     * the very FIRST poll while `steps` is still filling in, so dropping to
     * the 60s idle cadence right then leaves the waiting-window checklist
     * stale for a full minute at a time. When true, keep polling at
     * `activeIntervalMs` until BOTH `ready` is true AND the server's
     * `continue_gate` has left `waiting` (ticket #47: this now covers every
     * step, gating or not, not just the three original ones). Falls back to
     * `areStepsFinal` when `continue_gate` is absent (a degraded response).
     */
    holdActiveUntilStepsFinal?: boolean;
  } = {},
): UsePipelineStatus {
  const activeInterval = opts.activeIntervalMs ?? 4000;
  const idleInterval = opts.idleIntervalMs ?? 60000;
  const holdActiveUntilStepsFinal = opts.holdActiveUntilStepsFinal ?? false;
  const [status, setStatus] = useState<PatientPipelineStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelled = useRef(false);
  const readyRef = useRef(false);

  const id = patientId?.trim() || '';

  const tick = useCallback(async () => {
    if (!id || !isPatientPipelineApiAvailable()) {
      setLoading(false);
      return;
    }
    try {
      const next = await fetchPatientPipelineStatus(id);
      if (cancelled.current) return;
      const gate = next?.continue_gate;
      const stepsFinalNow = gate != null ? gate !== 'waiting' : areStepsFinal(next?.steps ?? []);
      readyRef.current = Boolean(next?.ready) && (!holdActiveUntilStepsFinal || stepsFinalNow);
      setStatus(next);
      setError(null);
    } catch (e) {
      if (cancelled.current) return;
      setError(e instanceof Error ? e.message : 'Could not load pipeline status');
    } finally {
      if (!cancelled.current) setLoading(false);
    }
  }, [id, holdActiveUntilStepsFinal]);

  useEffect(() => {
    cancelled.current = false;
    readyRef.current = false;
    setStatus(null);
    setError(null);
    if (!id) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const run = async () => {
      await tick();
      if (cancelled.current) return;
      const delay = readyRef.current ? idleInterval : activeInterval;
      timer.current = setTimeout(run, delay);
    };
    void run();

    return () => {
      cancelled.current = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [id, tick, activeInterval, idleInterval]);

  const reload = useCallback(() => {
    setLoading(true);
    void tick();
  }, [tick]);

  return { status, loading, error, ready: Boolean(status?.ready), reload };
}
